import React, { useMemo } from "react";
import moment from "moment";
import { isEmpty, map, orderBy } from "lodash";
import { Button, Drawer, Table } from "antd";
import { ColumnType } from "antd/lib/table";
import CustomDot from "components/CustomDot";
import { DATETIME_FORMAT, EMPTY } from "constants/common";
import {
  AICC_STATUS_DOT_SIZE,
  AICC_VERSION_EVENT_ACTION,
  AICC_VERSION_EVENT_LABEL,
  AICC_VERSION_STATUS,
  AICC_VERSION_STATUS_COLOR,
  AICC_VERSION_STATUS_LABEL,
} from "constants/aiccLanguage";
import { AiccLanguageVersion, AiccManagedLanguage } from "types/model/aiccLanguage";
import styles from "./Dialogs.module.scss";

interface Props {
  /** The language whose history is shown; absent keeps the drawer closed. */
  language?: AiccManagedLanguage;
  onClose: () => void;
  onRestore?: (version: AiccLanguageVersion) => void;
}

/**
 * Colour per act, so a version that is off the air is findable by scanning rather than by reading: amber
 * for the withdrawal, green for the resolution under it. A publish takes the body colour — it is the
 * normal case and colouring it too would leave nothing for the colours to mean.
 */
const EVENT_LINE_CLASS: Partial<Record<string, string>> = {
  [AICC_VERSION_EVENT_ACTION.withdrawn]: styles.versionWithdrawal,
  [AICC_VERSION_EVENT_ACTION.republished]: styles.versionRepublish,
};

const getColumns = (onRestore: Props["onRestore"]): ColumnType<AiccLanguageVersion>[] => [
  {
    title: "Version",
    dataIndex: "version",
    key: "version",
    width: 100,
    render: (_, { version }) => `V${version}`,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 140,
    render: (_, { status }) => (
      <CustomDot color={AICC_VERSION_STATUS_COLOR[status]} size={AICC_STATUS_DOT_SIZE}>
        {AICC_VERSION_STATUS_LABEL[status]}
      </CustomDot>
    ),
  },
  {
    title: "Release Notes",
    dataIndex: "releaseNotes",
    key: "releaseNotes",
    render: (_, { releaseNotes }) => releaseNotes || EMPTY,
  },
  {
    title: "Audit Trail",
    dataIndex: "publishedAt",
    key: "publishedAt",
    width: 240,
    /**
     * EVERY act on this version, oldest first — not a summary of the first and last.
     *
     * 🔴 THE THREE PAIRS IT USED TO READ COULD ONLY WITNESS THREE ACTS. `withdrawn_*` and `republished_*`
     * are single-valued, so a version taken down and put back three times (Albanian V4 on dev, seven
     * events) rendered two of them and silently dropped the rest — and which two depended on which
     * write the backend happened to keep. The sequence is the thing an operator opens this drawer for,
     * so the cell now walks `events` and the summary fields are a fallback inside the transformer.
     *
     * One block per act rather than one line: at this column's width an operator plus a timestamp wraps
     * anyway, so an explicit second line is both shorter and squarer than the wrap would be.
     */
    render: (_, { events }) =>
      isEmpty(events) ? (
        EMPTY
      ) : (
        <div>
          {map(events, (event, index) => (
            <div
              key={`${event.action}-${event.at}-${index}`}
              className={index ? styles.versionEventFollowing : undefined}
            >
              {/* An unknown action degrades to the backend's own word rather than to nothing, so a
                  fourth act the backend starts sending is legible here before this file knows it. */}
              <div className={EVENT_LINE_CLASS[event.action]}>
                {`${AICC_VERSION_EVENT_LABEL[event.action] ?? `${event.action} by`} ${event.by || "unknown"}`}
              </div>
              <div className={styles.versionTimestamp}>
                {event.at ? moment(event.at).format(DATETIME_FORMAT) : EMPTY}
              </div>
            </div>
          ))}
        </div>
      ),
  },
  ...(onRestore
    ? [
        {
          title: "Actions",
          dataIndex: "actions",
          key: "actions",
          width: 200,
          /**
           * AICC2-331 §5 AC-4, narrowed to a whitelist: only an archived version can be restored.
           *
           * 🔴 REPORTED FROM DEV. `!== public` was wrong, and the third status is what exposed it.
           * Unpublishing a language with no working copy hands its published version back as the
           * staged one (see `AICC_VERSION_STATUS`), so the row turned Publish-Ready AND grew a
           * Restore button — offering to restore the working copy from itself. A withdrawn language
           * gets back out through its card's own withdrawn row, not through here.
           */
          render: (_: unknown, version: AiccLanguageVersion) =>
            version.status === AICC_VERSION_STATUS.archived ? (
              <Button type="link" className={styles.linkButton} onClick={() => onRestore(version)}>
                Restore
              </Button>
            ) : (
              EMPTY
            ),
        },
      ]
    : []),
];

function VersionHistoryDrawer({ language, onClose, onRestore }: Props) {
  const columns = useMemo(() => getColumns(onRestore), [onRestore]);
  // Newest first: a publish adds its entry at the top of the list (AICC2-331 §5 AC-5).
  const versions = useMemo(() => orderBy(language?.versions ?? [], "version", "desc"), [language]);

  return (
    <Drawer
      title={`Version History${language ? ` — ${language.name}` : ""}`}
      width={880}
      visible={!!language}
      onClose={onClose}
      destroyOnClose
    >
      <Table<AiccLanguageVersion>
        columns={columns}
        dataSource={versions}
        rowKey={(record) => record.version}
        pagination={false}
        size="small"
      />
    </Drawer>
  );
}

export default VersionHistoryDrawer;
